import { supportChatDebug, supportChatWarn } from '../debug';
import { supportChatWsUrl } from './http';
/**
 * Parses the server envelope `{ "type": "...", "data": { ... } }`.
 * Falls back to `event` key and flat layout for forward-compat.
 */
function parseIncomingEvent(raw) {
    if (raw === null || typeof raw !== 'object')
        return null;
    const o = raw;
    const name = typeof o.type === 'string' ? o.type : typeof o.event === 'string' ? o.event : null;
    if (!name)
        return null;
    // Payload lives in `data`; fall back to the root object for flat layouts.
    const data = o.data !== undefined && o.data !== null && typeof o.data === 'object'
        ? { ...o, ...o.data }
        : o;
    return { name, data };
}
function asRecord(value) {
    return value !== null && typeof value === 'object' ? value : null;
}
function readConversationId(...records) {
    for (const record of records) {
        if (!record)
            continue;
        if (typeof record.conversation_id === 'string')
            return record.conversation_id;
        if (typeof record.conversationId === 'string')
            return record.conversationId;
        const conversation = asRecord(record.conversation);
        if (typeof conversation?.id === 'string')
            return conversation.id;
    }
    return undefined;
}
export function isSupportChatWsMessageEventName(name) {
    return (name === 'message' ||
        name === 'message_created' ||
        name === 'new_message' ||
        name === 'conversation_message' ||
        name === 'operator_message' ||
        name === 'agent_message');
}
/**
 * Customer sockets emit flat messages for customer/AI echoes and wrapped messages for operator replies.
 */
export function extractSupportChatWsMessage(data) {
    const payload = asRecord(data.payload) ?? asRecord(data.data);
    const wrappedMessage = asRecord(data.message) ?? asRecord(payload?.message);
    const messageRecord = wrappedMessage ?? payload ?? data;
    if (typeof messageRecord.id !== 'number')
        return null;
    const message = messageRecord;
    const conversationId = readConversationId(data, payload, messageRecord);
    if (!conversationId)
        return null;
    return { conversation_id: conversationId, message };
}
function isSupportAiStatus(value) {
    return (value === 'responded' ||
        value === 'unavailable' ||
        value === 'failed' ||
        value === 'queued_for_human' ||
        value === 'not_applicable');
}
/**
 * Opens the customer WebSocket. Call disconnect when the shell unmounts or identity changes.
 */
export function connectSupportChatSocket(actor, handlers) {
    let ws;
    let closedByUs = false;
    // URL resolution stays inside the try: a malformed base URL throws (http.ts) and must hit
    // the same onConnectionChange(false) fallback as a construct failure, not escape uncaught.
    try {
        const url = supportChatWsUrl(actor);
        supportChatDebug('WebSocket connecting', { actorKind: actor.kind });
        ws = new WebSocket(url);
    }
    catch (e) {
        supportChatWarn('WebSocket construct failed', { error: e, actorKind: actor.kind });
        handlers.onConnectionChange?.(false);
        return () => { };
    }
    ws.onopen = () => {
        supportChatDebug('WebSocket open');
        if (closedByUs)
            return;
        handlers.onConnectionChange?.(true);
    };
    ws.onclose = (event) => {
        supportChatWarn('WebSocket closed', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
        });
        if (closedByUs)
            return;
        handlers.onConnectionChange?.(false, { code: event.code, reason: event.reason });
    };
    ws.onerror = () => {
        supportChatWarn('WebSocket error event');
        if (closedByUs)
            return;
        handlers.onConnectionChange?.(false);
    };
    ws.onmessage = (ev) => {
        try {
            const raw = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
            const parsed = parseIncomingEvent(raw);
            if (!parsed)
                return;
            const { name, data } = parsed;
            const conversationId = readConversationId(data);
            supportChatDebug('WebSocket message received', { event: name, conversationId });
            if (isSupportChatWsMessageEventName(name)) {
                const extracted = extractSupportChatWsMessage(data);
                if (extracted) {
                    handlers.onMessage?.(extracted);
                }
                else {
                    supportChatWarn('WebSocket message event could not be parsed', { event: name, data });
                }
                return;
            }
            switch (name) {
                case 'message_deleted': {
                    if (conversationId && typeof data.message_id === 'number') {
                        handlers.onMessageDeleted?.({
                            conversation_id: conversationId,
                            message_id: data.message_id
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "message_deleted" event missing or invalid payload', {
                            data
                        });
                    }
                    break;
                }
                case 'handoff': {
                    if (conversationId) {
                        handlers.onHandoff?.({
                            conversation_id: conversationId,
                            payload: data
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "handoff" event missing conversation_id', { data });
                    }
                    break;
                }
                case 'assigned': {
                    if (conversationId) {
                        handlers.onAssigned?.({
                            conversation_id: conversationId,
                            agent_id: typeof data.agent_id === 'string' ? data.agent_id : undefined
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "assigned" event missing conversation_id', { data });
                    }
                    break;
                }
                case 'ai_status': {
                    if (conversationId && isSupportAiStatus(data.status)) {
                        handlers.onAiStatus?.({
                            conversation_id: conversationId,
                            status: data.status
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "ai_status" event missing or invalid payload', {
                            data
                        });
                    }
                    break;
                }
                case 'conversation_closed': {
                    if (conversationId) {
                        handlers.onConversationClosed?.({
                            conversation_id: conversationId,
                            resolved: data.resolved === true
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "conversation_closed" event missing conversation_id', {
                            data
                        });
                    }
                    break;
                }
                case 'conversation_reopened': {
                    if (conversationId) {
                        handlers.onConversationReopened?.({ conversation_id: conversationId });
                    }
                    else {
                        supportChatWarn('WebSocket "conversation_reopened" event missing conversation_id', {
                            data
                        });
                    }
                    break;
                }
                case 'server_shutdown': {
                    const reason = typeof data.message === 'string' ? data.message : undefined;
                    handlers.onServerShutdown?.(reason);
                    // Deliberately does not set closedByUs: the close must still report so the
                    // session layer's cooldown reconnect (onConnectionChange(false)) runs.
                    ws.close();
                    break;
                }
                default:
                    supportChatDebug('WebSocket message ignored', { event: name });
                    break;
            }
        }
        catch (e) {
            supportChatWarn('WebSocket message parse failed', { error: e });
        }
    };
    return () => {
        closedByUs = true;
        try {
            supportChatDebug('WebSocket disconnect requested');
            ws.close();
        }
        catch {
            /* noop */
        }
    };
}
